import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Upload, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fileToBase64 } from "@/lib/uploads";
import { format, parseISO } from "date-fns";

const HEALTH = {
  healthy: { label: "Healthy", color: "text-emerald-800 bg-emerald-100 border-emerald-200", icon: CheckCircle2 },
  watch: { label: "Watch", color: "text-amber-900 bg-amber-100 border-amber-200", icon: Sparkles },
  concerning: { label: "Concerning", color: "text-orange-900 bg-orange-100 border-orange-200", icon: AlertTriangle },
  critical: { label: "Critical", color: "text-red-900 bg-red-100 border-red-200", icon: AlertTriangle },
};

export default function Diagnose() {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [hives, setHives] = useState([]);
  const [hiveId, setHiveId] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/diagnoses").then((r) => setHistory(r.data));

  useEffect(() => {
    api.get("/hives").then((r) => {
      setHives(r.data);
      if (user?.default_hive_id && r.data.find((h) => h.id === user.default_hive_id)) {
        setHiveId(user.default_hive_id);
      }
    });
    loadHistory();
    // eslint-disable-next-line
  }, [user?.default_hive_id]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await fileToBase64(file);
    setB64(data);
    setPreview(data);
    setResult(null);
  };

  const diagnose = async () => {
    if (!b64) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/diagnose", {
        image_base64: b64,
        hive_id: hiveId || null,
        context,
      });
      setResult(data);
      loadHistory();
      toast.success("Diagnosis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnosis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="diagnose-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">AI Vision</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Diagnose your hive</h1>
        <p className="text-stone-500 mt-2 max-w-2xl">Upload a photo of bees, brood frames, or the hive. Claude 4.5 will flag common issues like mites, disease, or queen problems.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
          <label className="block aspect-[4/3] border-2 border-dashed border-stone-300 rounded-2xl grid place-items-center cursor-pointer hover:border-yellow-400 hover:bg-yellow-50/50 transition-colors overflow-hidden" data-testid="diagnose-dropzone">
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center">
                <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                <div className="text-sm text-stone-600">Click to upload a photo</div>
                <div className="text-xs text-stone-400 mt-1">JPG, PNG or WEBP</div>
              </div>
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" data-testid="diagnose-file-input" />
          </label>

          <div>
            <Label>Linked hive (optional)</Label>
            <Select value={hiveId || "none"} onValueChange={(v) => setHiveId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="diagnose-hive-select"><SelectValue placeholder="Select hive" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No hive</SelectItem>
                {hives.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Extra context</Label>
            <Textarea rows={3} maxLength={2000} value={context} onChange={(e) => setContext(e.target.value)} placeholder="e.g. bees seem agitated since last week, dead bees on landing board..." data-testid="diagnose-context-input" />
          </div>
          <Button onClick={diagnose} disabled={!b64 || loading} className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="diagnose-run-button">
            <Camera className="w-4 h-4 mr-2" />
            {loading ? "Analyzing…" : "Run diagnosis"}
          </Button>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 min-h-[400px]">
          {!result && !loading && (
            <div className="h-full grid place-items-center text-stone-400 text-sm">
              <div className="text-center">
                <Sparkles className="w-6 h-6 mx-auto mb-2" />
                Your AI diagnosis will appear here.
              </div>
            </div>
          )}
          {loading && (
            <div className="h-full grid place-items-center text-stone-500 text-sm">
              <div className="text-center">
                <div className="w-10 h-10 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                Reading the frames…
              </div>
            </div>
          )}
          {result && <DiagnosisView result={result} />}
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-medium mb-3">Past diagnoses</h2>
        {history.length === 0 ? (
          <div className="text-sm text-stone-500">Nothing yet.</div>
        ) : (
          <div className="space-y-3" data-testid="diagnosis-history">
            {history.map((d) => {
              const h = HEALTH[d.health_status] || HEALTH.watch;
              return (
                <div key={d.id} className="bg-white border border-stone-200 rounded-2xl p-5 flex gap-4" data-testid={`diagnosis-item-${d.id}`}>
                  {d.image_url && (
                    <img src={d.image_url} alt="" className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl border border-stone-200 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${h.color}`}>
                        <h.icon className="w-3 h-3" /> {h.label}
                      </span>
                      <span className="text-xs text-stone-500">{format(parseISO(d.created_at), "MMM d, yyyy · h:mm a")}</span>
                    </div>
                    <p className="text-stone-800 mt-2">{d.summary}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function DiagnosisView({ result }) {
  const h = HEALTH[result.health_status] || HEALTH.watch;
  return (
    <div className="space-y-5" data-testid="diagnosis-result">
      <div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border ${h.color}`}>
          <h.icon className="w-3.5 h-3.5" /> {h.label}
        </span>
      </div>
      <p className="text-stone-900 leading-relaxed">{result.summary}</p>

      {result.issues?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Issues spotted</div>
          <ul className="space-y-2">
            {result.issues.map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-stone-800">
                <AlertTriangle className="w-3.5 h-3.5 text-orange-600 mt-0.5 shrink-0" />
                {it}
              </li>
            ))}
          </ul>
        </div>
      )}
      {result.recommendations?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Recommendations</div>
          <ul className="space-y-2">
            {result.recommendations.map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-stone-800">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                {it}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
